import * as yup from 'yup';

import { FormErrors } from '@blockpulse3/data/shared';
import {
  CompanyDraftContent,
  DraftAddress,
  DraftAgent,
  DraftRepresentative,
  DraftShareholder,
  DraftTarget,
  DraftUbo,
  IdentityType,
  RepresentativeRole,
  UboRole,
} from '@blockpulse3/graphql/hooks';
import {
  requiredBirthdateField,
  requiredBooleanField,
  requiredEmailField,
  requiredIntegerNumberField,
  requiredNumberField,
  requiredPercentageNumberField,
  requiredSelectField,
  requiredStringField,
} from '@blockpulse3/web-client/forms';
import {
  INaturalShareholderForm,
  ISharePercentageForm,
} from '../Forms/ShareholderInformationsForm';

const requiredSelectDataField = yup.object({
  label: requiredStringField,
  value: requiredStringField,
});

export const schemaAddress = yup.object({
  line: requiredStringField,
  city: requiredStringField,
  postalCode: requiredStringField,
  country: requiredStringField,
  countryData: requiredSelectField,
});

export const draftAddressDefaultValues: DraftAddress = {
  line: '',
  city: '',
  postalCode: '',
  country: 'FRA',
  countryData: { label: '', value: '' },
};

export const schemaTarget = yup.object({
  name: requiredStringField,
  registrationNumber: requiredStringField,
  registrationEntity: requiredStringField,
});

export const draftTargetDefaultValues: DraftTarget = {
  name: '',
  registrationNumber: '',
  registrationEntity: '',
};

const schemaIndividualRepresentativeDef = {
  role: requiredStringField,
  type: requiredStringField,
  address: schemaAddress,
  firstName: requiredStringField,
  lastName: requiredStringField,
  email: requiredEmailField,
  birthdate: requiredBirthdateField,
  birthplace: requiredStringField,
  nationalityData: requiredSelectDataField,
};
const schemaCompanyRepresentativeDef = {
  role: requiredStringField,
  type: requiredStringField,
  name: requiredStringField,
  corporateForm: requiredStringField,
  shareCapital: requiredNumberField,
  registrationEntity: requiredStringField,
  registrationNumber: requiredStringField,
  address: schemaAddress,
  firstName: requiredStringField,
  lastName: requiredStringField,
  position: requiredStringField,
  email: requiredEmailField,
};

export const schemaCompanyRepresentative = yup.object(schemaCompanyRepresentativeDef);
export const schemaCompanyPresident = yup.object({
  ...schemaCompanyRepresentativeDef,
  revocationMajorityData: requiredSelectDataField,
});

/* ** SPV ** */
export const spvNaturalRepresentativeSchema = yup.object({
  ...schemaIndividualRepresentativeDef,
  fatherFirstName: requiredStringField,
  fatherLastName: requiredStringField,
  motherFirstName: requiredStringField,
  motherLastName: requiredStringField,
}) as yup.ObjectSchema<DraftRepresentative>;
export const spvNaturalRepresentativePresidentSchema = yup.object({
  ...schemaIndividualRepresentativeDef,
  fatherFirstName: requiredStringField,
  fatherLastName: requiredStringField,
  motherFirstName: requiredStringField,
  motherLastName: requiredStringField,
  revocationMajorityData: requiredSelectDataField,
}) as yup.ObjectSchema<DraftRepresentative>;
export const spvLegalRepresentativeSchema = yup.object(
  schemaCompanyRepresentativeDef,
) as yup.ObjectSchema<DraftRepresentative>;
export const spvLegalRepresentativePresidentSchema = yup.object({
  ...schemaCompanyRepresentativeDef,
  revocationMajorityData: requiredSelectDataField,
}) as yup.ObjectSchema<DraftRepresentative>;

export const schemaIndividualRepresentative = yup.object(schemaIndividualRepresentativeDef);
export const schemaIndividualPresident = yup.object({
  ...schemaIndividualRepresentativeDef,
  revocationMajorityData: requiredSelectDataField,
});

export const draftRepresentativeDefaultValues: DraftRepresentative = {
  role: '',
  type: IdentityType.COMPANY,
  name: '',
  creationDate: '',
  registrationNumber: '',
  registrationEntity: '',
  corporateForm: '',
  nominalValue: 0,
  shareCapital: 0,
  address: draftAddressDefaultValues,
  firstName: '',
  lastName: '',
  position: '',
  email: '',
  birthdate: '',
  birthplace: '',
  nationalityData: { label: '', value: '' },
  fatherFirstName: '',
  fatherLastName: '',
  motherFirstName: '',
  motherLastName: '',
  revocationMajorityData: { label: '', value: '' },
};

export const schemaUbo = yup.object({
  firstName: requiredStringField,
  lastName: requiredStringField,
  role: requiredStringField,
  birthdate: requiredBirthdateField,
  birthplace: requiredStringField,
  birthCityPostalCode: requiredStringField,
  birthCountryData: requiredSelectField,
  address: schemaAddress,
  nationalityData: requiredSelectDataField,
  isDirect: requiredBooleanField,
  sharePercentage: requiredNumberField,
  // email: requiredStringField,
  // birthCountry: requiredStringField,
  // startingDate: requiredStringField,
  // votingRightsPercentage: requiredNumberField,
});

export const draftUboDefaultValues: DraftUbo = {
  role: UboRole.LegalRepresentative,
  address: draftAddressDefaultValues,
  firstName: '',
  lastName: '',
  birthdate: '',
  birthplace: '',
  birthCityPostalCode: '',
  birthCountryData: { label: '', value: '' },
  nationalityData: { label: '', value: '' },
  startingDate: '',
  isDirect: false,
  sharePercentage: 25.01,
  votingRightsPercentage: 25.01,
};

export const schemaPreferredShares = yup.object({
  percentage: requiredPercentageNumberField(0.01),
}) as yup.ObjectSchema<ISharePercentageForm>;

export const legalShareholderSchema = yup.object({
  type: requiredStringField,
  name: requiredStringField,
  registrationNumber: requiredStringField,
  registrationEntity: yup.string().trim(), //.required(FormErrors.RequiredField),
  corporateForm: requiredStringField,
  shareCapital: requiredNumberField,
  address: schemaAddress,
  representative: yup.object({
    firstName: requiredStringField,
    lastName: requiredStringField,
    position: requiredStringField,
    email: requiredEmailField,
  }),
  ubos: yup.array().of(schemaUbo),
  // firstName: requiredStringField,
  // lastName: requiredStringField,
  // position: requiredStringField,
  // email: requiredEmailField,
  // birthdate: requiredBirthdateField,
  // birthplace: requiredStringField,
  // nationalityData: requiredSelectDataField,
  preferredSharesPercentages: requiredPercentageNumberField(0),
  capitalDeposit: requiredIntegerNumberField,
}) as yup.ObjectSchema<DraftShareholder>;

export const naturalShareholderSchema = yup.object({
  type: requiredStringField,
  capitalDeposit: requiredIntegerNumberField,
  firstName: requiredStringField,
  lastName: requiredStringField,
  email: requiredEmailField,
  birthdate: requiredBirthdateField,
  address: schemaAddress,
  nationalityData: requiredSelectDataField,
  fatherFirstName: requiredStringField,
  fatherLastName: requiredStringField,
  motherFirstName: requiredStringField,
  motherLastName: requiredStringField,
  residenceCertificate: requiredStringField,
}) as yup.ObjectSchema<INaturalShareholderForm>;

export const draftShareholderDefaultValues: DraftShareholder = {
  type: IdentityType.COMPANY,
  name: '',
  creationDate: '',
  registrationNumber: '',
  registrationEntity: '',
  corporateForm: '',
  shareCapital: 0,
  address: draftAddressDefaultValues,
  representative: draftRepresentativeDefaultValues,
  ubos: [draftUboDefaultValues],
  firstName: '',
  lastName: '',
  position: '',
  email: '',
  birthdate: '',
  birthplace: '',
  nationalityData: { label: '', value: '' },
  capitalDeposit: 0,
  preferredSharesPercentages: 0,
  fatherFirstName: '',
  fatherLastName: '',
  motherFirstName: '',
  motherLastName: '',
  // ordinarySharesCount: 0,
  // preferredSharesCount: 0,
};

export const schemaAgent = yup.object({
  firstName: requiredStringField,
  lastName: requiredStringField,
  email: requiredEmailField,
}) as yup.ObjectSchema<DraftAgent>;

export const draftAgentDefaultValues: DraftAgent = {
  role: RepresentativeRole.VIEWER,
  firstName: '',
  lastName: '',
  email: '',
};

export const schemaLegal = yup.object({
  name: requiredStringField,
  address: schemaAddress,
  target: schemaTarget,
}) as yup.ObjectSchema<CompanyDraftContent>;

export const schemaLegalExistingCompany = yup.object({
  name: requiredStringField,
  address: schemaAddress,
  creationDate: requiredBirthdateField,
  // countryData: requiredSelectDataField,
  registrationNumber: requiredStringField,
  registrationEntity: requiredStringField,
  corporateForm: requiredStringField,
  shareCapital: requiredNumberField,
});

export const schemaFinancial = yup.object({
  compensation: yup
    .number()
    .typeError(FormErrors.RequiredField)
    .min(0, FormErrors.PositiveFormat)
    .required(FormErrors.RequiredField),
  carriedInterest: yup
    .number()
    .typeError(FormErrors.RequiredField)
    .min(0, FormErrors.CarriedPercentageFormat)
    .max(30, FormErrors.CarriedPercentageFormat)
    .required(FormErrors.RequiredField),
}) as yup.ObjectSchema<CompanyDraftContent>;

export const companyDraftContentDefaultValues: CompanyDraftContent = {
  name: '',
  address: draftAddressDefaultValues,
  creationDate: '',
  countryData: { label: '', value: '' },
  registrationNumber: '',
  registrationEntity: '',
  // corporateForm: '',
  // businessActivity: '',
  // businessActivityDescription: '',
  // bankMonthlyPaymentVolume: '',
  shareCapital: 0,
  compensation: 0,
  carriedInterest: 0,
  target: draftTargetDefaultValues,
  representatives: [draftRepresentativeDefaultValues],
  initialShareholder: draftShareholderDefaultValues,
  agents: [],
};
